import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const CATEGORIES = ['student', 'resident', 'staff', 'visitor'];
const CAT_COLORS = { student: 'blue', resident: 'purple', staff: 'emerald', visitor: 'amber' };

const emptyForm = {
  full_name: '', category: 'student', email: '', phone: '',
  building: '', department: '', room_number: '',
  accommodation_start: '', accommodation_end: '',
  meals_per_day: 3, meal_breakfast: true, meal_lunch: true, meal_dinner: true,
  notes: '',
};

export default function Cardholders({ isAdmin }) {
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchCardholders(); }, []);

  const fetchCardholders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('restaurant_cardholders')?.select('*, restaurant_nfc_cards(card_uid, is_active)')?.order('full_name');
      if (!error) setCardholders(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form?.full_name?.trim()) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = { ...form, created_by: user?.id };
      let result;
      if (editId) {
        result = await supabase?.from('restaurant_cardholders')?.update(payload)?.eq('id', editId);
      } else {
        result = await supabase?.from('restaurant_cardholders')?.insert(payload);
      }
      if (!result?.error) {
        setShowForm(false);
        setEditId(null);
        setForm(emptyForm);
        fetchCardholders();
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const openEdit = (ch) => {
    setEditId(ch?.id);
    setForm({
      full_name: ch?.full_name || '', category: ch?.category || 'student',
      email: ch?.email || '', phone: ch?.phone || '',
      building: ch?.building || '', department: ch?.department || '', room_number: ch?.room_number || '',
      accommodation_start: ch?.accommodation_start || '', accommodation_end: ch?.accommodation_end || '',
      meals_per_day: ch?.meals_per_day || 3,
      meal_breakfast: ch?.meal_breakfast ?? true, meal_lunch: ch?.meal_lunch ?? true, meal_dinner: ch?.meal_dinner ?? true,
      notes: ch?.notes || '',
    });
    setShowForm(true);
    setSelected(null);
  };

  const toggleActive = async (ch) => {
    await supabase?.from('restaurant_cardholders')?.update({ is_active: !ch?.is_active })?.eq('id', ch?.id);
    fetchCardholders();
  };

  const filtered = cardholders?.filter(c => {
    const matchCat = filterCat === 'all' || c?.category === filterCat;
    const matchSearch = !search || c?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) || c?.email?.toLowerCase()?.includes(search?.toLowerCase()) || c?.building?.toLowerCase()?.includes(search?.toLowerCase());
    return matchCat && matchSearch;
  });

  const isExpired = (ch) => ch?.accommodation_end && new Date(ch.accommodation_end) < new Date();

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e?.target?.value)}
            className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-44" />
          <select value={filterCat} onChange={e => setFilterCat(e?.target?.value)}
            className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="all">All Categories</option>
            {CATEGORIES?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
          </select>
          <span className="text-xs text-muted-foreground">{filtered?.length} record(s)</span>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); setSelected(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all">
            <Icon name="UserPlus" size={14} />Add Cardholder
          </button>
        )}
      </div>
      {showForm && isAdmin && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-foreground">{editId ? 'Edit Cardholder' : 'New Cardholder'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Full Name *</label>
              <input type="text" value={form?.full_name} onChange={e => setForm(p => ({ ...p, full_name: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
              <select value={form?.category} onChange={e => setForm(p => ({ ...p, category: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {CATEGORIES?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
              <input type="email" value={form?.email} onChange={e => setForm(p => ({ ...p, email: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Building</label>
              <input type="text" value={form?.building} onChange={e => setForm(p => ({ ...p, building: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Department</label>
              <input type="text" value={form?.department} onChange={e => setForm(p => ({ ...p, department: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Room Number</label>
              <input type="text" value={form?.room_number} onChange={e => setForm(p => ({ ...p, room_number: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Accommodation Start</label>
              <input type="date" value={form?.accommodation_start} onChange={e => setForm(p => ({ ...p, accommodation_start: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Accommodation End</label>
              <input type="date" value={form?.accommodation_end} onChange={e => setForm(p => ({ ...p, accommodation_end: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Meals Per Day</label>
              <input type="number" min={1} max={3} value={form?.meals_per_day} onChange={e => setForm(p => ({ ...p, meals_per_day: parseInt(e?.target?.value) }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">Meal Eligibility</label>
            <div className="flex gap-4">
              {['breakfast', 'lunch', 'dinner']?.map(m => (
                <label key={m} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form?.[`meal_${m}`]} onChange={e => setForm(p => ({ ...p, [`meal_${m}`]: e?.target?.checked }))}
                    className="rounded border-border text-primary focus:ring-primary/30" />
                  <span className="text-sm capitalize text-foreground">{m}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !form?.full_name?.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center gap-2 hover:opacity-90 transition-all">
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Save" size={13} />}
              {editId ? 'Update' : 'Add Cardholder'}
            </button>
            <button onClick={() => { setShowForm(false); setEditId(null); }}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-all">Cancel</button>
          </div>
        </div>
      )}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Icon name="Loader2" size={22} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Building</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Meals</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">NFC Card</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stay</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  {isAdmin && <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(ch => (
                  <tr key={ch?.id} className="hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => setSelected(selected?.id === ch?.id ? null : ch)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{ch?.full_name}</div>
                      {ch?.email && <div className="text-xs text-muted-foreground">{ch?.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium bg-${CAT_COLORS?.[ch?.category]}-100 text-${CAT_COLORS?.[ch?.category]}-700 capitalize`}>{ch?.category}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{ch?.building || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {ch?.meal_breakfast && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">B</span>}
                        {ch?.meal_lunch && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">L</span>}
                        {ch?.meal_dinner && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">D</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {ch?.restaurant_nfc_cards?.[0]?.card_uid || <span className="italic">No card</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {ch?.accommodation_end ? (
                        <span className={isExpired(ch) ? 'text-red-500 font-medium' : ''}>{ch?.accommodation_end}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ch?.is_active && !isExpired(ch) ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {isExpired(ch) ? 'Expired' : ch?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right" onClick={e => e?.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(ch)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                            <Icon name="Pencil" size={13} />
                          </button>
                          <button onClick={() => toggleActive(ch)} className={`p-1.5 rounded-lg hover:bg-muted transition-colors ${ch?.is_active ? 'text-amber-500' : 'text-emerald-500'}`}>
                            <Icon name={ch?.is_active ? 'PauseCircle' : 'PlayCircle'} size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="py-10 text-center text-sm text-muted-foreground">No cardholders found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Detail Panel */}
      {selected && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">{selected?.full_name}</h3>
            <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><Icon name="X" size={14} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Category</div><div className="font-medium capitalize">{selected?.category}</div></div>
            <div><div className="text-xs text-muted-foreground">Department</div><div className="font-medium">{selected?.department || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Room</div><div className="font-medium">{selected?.room_number || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Phone</div><div className="font-medium">{selected?.phone || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Check-in</div><div className="font-medium">{selected?.accommodation_start || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Check-out</div><div className="font-medium">{selected?.accommodation_end || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Meals/Day</div><div className="font-medium">{selected?.meals_per_day}</div></div>
            <div><div className="text-xs text-muted-foreground">NFC Card</div><div className="font-mono text-xs font-medium">{selected?.restaurant_nfc_cards?.[0]?.card_uid || 'None'}</div></div>
          </div>
          {selected?.notes && <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">{selected?.notes}</div>}
        </div>
      )}
    </div>
  );
}
